import "./shipSellModal.css";
import Toaster from "../Toast/Toast";
import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { setCart } from "../../redux/slices/cartSlice";
import BackdropLoader from "../BackdropLoader/BackdropLoader";
import { updateUserData } from "../../redux/slices/authSlice";
import { syncUserData } from "../../services/userSync.service";
import {
  getCart,
  sellItem,
  shipItems,
  userVerificationEmail,
} from "../../api/modules/b2droppie.class";
import CommonModal from "../CommonModal/CommonModal";

const ShipSellModal = ({
  show,
  type,
  item,
  setModalShow,
  setNavigationToast,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state?.auth?.user);
  const [error, setError] = useState({ success: "", error: "" });
  const isDarkMode = useSelector((state) => state.theme.darkMode);
  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);

  const isSell = type === "sell";
  const isMultiple = Array.isArray(item);

  useEffect(() => {
    if (!show) {
      setError({ success: "", error: "" });
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [show]);

  const confirmHandler = async () => {
    if (loading) return;

    const items = isMultiple ? item : [item];

    const product_id = items.map((i) => i.id);
    const quantities = items.reduce((acc, i) => {
      acc[i.id] = i.quantity || 1;
      return acc;
    }, {});

    setLoading(true);
    try {
      const actionFn = isSell ? sellItem : shipItems;
      const response = await actionFn({ product_id, quantities });

      const successMessage = response?.data?.message;
      const errorMessage = response?.data?.error;

      if (response?.data?.success) {
        setError({
          success: successMessage,
          error: "",
        });

        const updated = await syncUserData();
        if (updated) {
          dispatch(updateUserData(updated));
        }

        // ✅ Refresh cart and store in Redux
        const updatedCart = await getCart({ id: user?.id });
        const apiData = updatedCart?.data;

        const items = apiData?.data?.map((item) => ({
          ...item,
          stock: item.quantity,
          quantity: item.quantity,
        }));

        const itemsCount = items.reduce(
          (sum, item) => sum + (item.quantity || 0),
          0,
        );

        const totalPrice = items.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0,
        );

        dispatch(
          setCart({
            items,
            currentPage: apiData.current_page,
            lastPage: apiData.last_page,
            perPage: apiData.per_page,
            total: apiData.total,
            links: apiData.links,
            itemsCount,
            totalPrice,
          }),
        );

        setTimeout(() => {
          setModalShow({ show: false, type: "" });
        }, 300);
      }

      if (errorMessage === "Shipping address required") {
        setNavigationToast({
          success: successMessage,
          error: errorMessage,
          redirectTo: "/myaccount",
        });
      }

      if (errorMessage) {
        setError({ error: errorMessage });
      }
    } catch (e) {
      if (e?.status === 403) {
        setShowVerifyEmailModal(true);
      }
      setError({
        error:
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const userEmailVerification = async () => {
    try {
      const response = await userVerificationEmail({
        email: user?.email,
      });
      setError({
        success: response?.data?.message || "",
        error: response?.data?.error?.message || "",
      });
      if (response?.data?.success) {
        setShowVerifyEmailModal(false);
        setTimeout(() => {
          setModalShow({ show: false, type: "" });
        }, 1000);
      }
    } catch (err) {
      console.error(err);
      setError({
        success: "",
        error: "Something went wrong. Try again.",
      });
    } finally {
      setLoading(false);
      setShowVerifyEmailModal(false);
      setTimeout(() => {
        setModalShow({ show: false, type: "" });
      }, 1000);
    }
  };

  if (!show) return null;

  return (
    <>
      <Toaster message={error} />
      <div
        onClick={() => setModalShow({ show: false, type: "" })}
        className={`simple-modal-overlay ${isDarkMode ? "dark" : "light"}`}
      >
        <div className="final-sell-modal" onClick={(e) => e.stopPropagation()}>
          <span
            className="final-sell-modal-close"
            onClick={() => setModalShow({ show: false, type: "" })}
          >
            &times;
          </span>

          <div className="final-sell-modal-body">
            <div className="final-sell-warning-icon">
              <AlertTriangle
                size={42}
                strokeWidth={2}
                color={isDarkMode ? "white" : "black"}
              />
            </div>

            <h6 className="final-sell-msg">
              Are you sure you want to {type} the following item
              {isMultiple ? "s" : ""}?
            </h6>

            {(isMultiple ? item : [item]).map((i) => (
              <div key={i.id} className="d-center flex-column">
                <span className="text-center">{i.name}</span>
                <span className="text-center my-3">
                  Quantity: <span className="quantity-count">{i.quantity}</span>
                </span>
              </div>
            ))}

            <div className="final-sell-buttons">
              <button
                disabled={loading}
                onClick={confirmHandler}
                className={`${isDarkMode ? "outlined" : "filled"}-medium-deep-purple-btn-dark`}
              >
                {loading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <CommonModal
        show={showVerifyEmailModal}
        onClose={() => setShowVerifyEmailModal(false)}
        onConfirm={() => userEmailVerification()}
        title={
          <h4
            className={`${isDarkMode ? "text-light-lavender-purple" : "text-deep-purple"} w-100 d-center`}
          >
            Email not Verified!
          </h4>
        }
        body={
          <>
            <p
              className={`${isDarkMode ? "text-muter-lavender-purple" : "text-charcoal-gray"} mb-2`}
            >
              Please verify your email address. Click Here to generate an email.
            </p>
          </>
        }
        showFooter={true}
        modalWidth="w-lg-35"
        isDarkMode={isDarkMode}
        confirmText="Send Email!"
        btnStyle="filled-danger-btn-dark"
      />

      <BackdropLoader show={loading} />
    </>
  );
};

export default ShipSellModal;
